/**
 * SMART Goal Wizard - Conversational, Step-by-Step Goal Creation
 * Clean, modern design - No emoji overload
 * Teaches planning while being engaging
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Check, Target, Calendar,
  Lightbulb, TrendingUp, CheckCircle, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const SMART_STEPS = [
  {
    id: 'title',
    label: 'Your Goal',
    question: "What do you want to achieve?",
    placeholder: "e.g., Improve my professional communication skills",
    help: "Think about what you want to accomplish by the end of summer. Be ambitious but realistic.",
    icon: Target
  },
  {
    id: 'specific',
    label: 'Specific',
    question: "What exactly will you do? Break it down.",
    placeholder: "e.g., Practice writing professional emails daily and present to my team weekly",
    help: "Be specific about actions, not just outcomes. What will you actually DO?",
    examples: [
      "Present to team 5 times",
      "Write 20 professional emails", 
      "Complete 10 Excel projects"
    ],
    icon: Lightbulb
  },
  {
    id: 'measurable',
    label: 'Measurable',
    question: "How will you know you succeeded?",
    placeholder: "e.g., Successfully send 20 professional emails and get positive feedback from supervisor",
    help: "What evidence will prove you achieved this? Numbers, feedback, completed tasks?",
    examples: [
      "Complete 5 presentations with positive feedback",
      "Achieve 90% accuracy rate",
      "Get supervisor sign-off on all tasks"
    ],
    icon: TrendingUp
  },
  {
    id: 'achievable',
    label: 'Achievable',
    question: "What makes this realistic for you?",
    placeholder: "e.g., Yes - my supervisor offered to coach me and I have email templates to reference",
    help: "Think about your resources, support, and time. What will help you succeed?",
    examples: [
      "Supervisor will mentor me",
      "I have tools/resources available",
      "I can practice during work hours"
    ],
    icon: CheckCircle
  },
  {
    id: 'relevant',
    label: 'Relevant',  
    question: "Why does this matter to your future?",
    placeholder: "e.g., Professional communication is essential for any career and will help with college applications",
    help: "Connect this to your bigger picture. How does this goal serve your future?",
    examples: [
      "Essential for college applications",
      "Required for my dream career",
      "Builds foundation for future success"
    ],
    icon: Sparkles
  }
];

const SmartGoalWizard = ({ onComplete, onCancel, interests = [], skills = [] }) => {
  const { colors } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    specific: '',
    measurable: '',
    achievable: '',
    relevant: '',
    deadline: '',
    goal_type: 'summer',
    visibility: 'public'
  });
  const [aiSuggestion, setAiSuggestion] = useState(null);

  const currentStepData = SMART_STEPS[currentStep];
  const isLastStep = currentStep === SMART_STEPS.length;
  const canProceed = currentStep === 0 
    ? formData.title.trim().length > 0
    : formData[currentStepData?.id]?.trim().length > 0;

  const handleNext = () => {
    if (canProceed && currentStep < SMART_STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.deadline) {
      toast.error('Please set a deadline');
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/goals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const goal = await response.json();
        toast.success('Goal created! +10 points');
        onComplete?.(goal);
      } else {
        toast.error('Failed to create goal');
      }
    } catch (error) {
      toast.error('Error creating goal');
    }
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {SMART_STEPS.map((step, idx) => (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center">
            <div 
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                idx < currentStep ? 'bg-green-500 text-white' :
                idx === currentStep ? 'bg-blue-600 text-white' :
                'bg-gray-200 text-gray-400'
              }`}
            >
              {idx < currentStep ? <Check className="w-5 h-5" /> : idx + 1}
            </div>
            <p className="text-xs mt-2 font-medium" style={{ 
              color: idx <= currentStep ? colors.text : colors.text_muted 
            }}>
              {step.label}
            </p>
          </div>
          {idx < SMART_STEPS.length - 1 && (
            <div className={`flex-1 h-1 mx-2 rounded transition-all ${
              idx < currentStep ? 'bg-green-500' : 'bg-gray-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Card 
        className="max-w-3xl mx-auto p-8"
        style={{ backgroundColor: colors.card, borderColor: colors.border }}
      >
        <StepIndicator />

        <AnimatePresence mode="wait">
          {!isLastStep ? (
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  {currentStepData && (
                    <>
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}
                      >
                        <currentStepData.icon className="w-6 h-6" style={{ color: '#3B82F6' }} />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
                          {currentStepData.question}
                        </h2>
                        <p className="text-sm" style={{ color: colors.text_secondary }}>
                          {currentStepData.help}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {currentStep === 0 ? (
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={currentStepData.placeholder}
                    className="text-lg p-4"
                    style={{ 
                      backgroundColor: colors.surface, 
                      borderColor: colors.border, 
                      color: colors.text 
                    }}
                    autoFocus
                  />
                ) : (
                  <Textarea
                    value={formData[currentStepData.id]}
                    onChange={(e) => setFormData({ ...formData, [currentStepData.id]: e.target.value })}
                    placeholder={currentStepData.placeholder}
                    rows={4}
                    className="text-base"
                    style={{ 
                      backgroundColor: colors.surface, 
                      borderColor: colors.border, 
                      color: colors.text 
                    }}
                    autoFocus
                  />
                )}

                {currentStepData?.examples && (
                  <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
                    <p className="text-sm font-semibold mb-2" style={{ color: colors.text }}>
                      Examples to guide you:
                    </p>
                    <ul className="space-y-1">
                      {currentStepData.examples.map((ex, idx) => (
                        <li key={idx} className="text-sm flex items-start gap-2" style={{ color: colors.text_secondary }}>
                          <span className="text-blue-500 mt-0.5">•</span>
                          <span>{ex}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    style={{ borderColor: colors.border, color: colors.text }}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={!canProceed}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                >
                  {currentStep === SMART_STEPS.length - 1 ? 'Review Goal' : 'Continue'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4" style={{ color: colors.text }}>
                  Review Your SMART Goal
                </h2>
                
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: colors.text_secondary }}>
                      Goal
                    </p>
                    <p className="text-xl font-bold" style={{ color: colors.text }}>
                      {formData.title}
                    </p>
                  </div>

                  {/* SMART Breakdown */}
                  {SMART_STEPS.map(step => (
                    step.id !== 'title' && formData[step.id] && (
                      <div key={step.id} className="p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
                        <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: colors.text_secondary }}>
                          {step.label}
                        </p>
                        <p className="text-sm leading-relaxed" style={{ color: colors.text }}>
                          {formData[step.id]}
                        </p>
                      </div>
                    )
                  ))}

                  {/* Deadline */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: colors.text_secondary }}>
                      Deadline
                    </p>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" style={{ color: colors.primary }} />
                      <Input
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                        style={{ 
                          backgroundColor: colors.surface, 
                          borderColor: colors.border, 
                          color: colors.text 
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Validation */}
              <div className="mb-6 p-4 rounded-lg" style={{ 
                backgroundColor: 'rgba(139,92,246,0.05)', 
                border: '1px solid rgba(139,92,246,0.2)' 
              }}>
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 mt-0.5" style={{ color: '#8B5CF6' }} />
                  <div>
                    <p className="text-sm font-semibold mb-1" style={{ color: colors.text }}>
                      AI Analysis
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: colors.text }}>
                      This is a well-structured SMART goal! Your specific actions are clear, 
                      your measurement criteria are concrete, and your timeline is realistic. 
                      This goal builds on your current skills and connects to your future. 
                      You're setting yourself up for success.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  style={{ borderColor: colors.border, color: colors.text }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Create Goal (+10 points)
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
};

export default SmartGoalWizard;
